// The store's reviews: the review-prompt settings (an enabled funnel needs
// an https Google review link) and the review stream, filtered by rating
// and date range on the server.
import { useCallback, useEffect, useRef, useState } from 'react';
import { Star } from 'lucide-react';
import { ApiError, api, dateTimeOf } from '../../lib/api';
import { useBase, useScope } from '../../scope';
import { messageOf, type Review, type ReviewSettings } from './types';

function Stars(props: { rating: number; size?: number }) {
  return (
    <span style={{ display: 'inline-flex', gap: 2, color: 'var(--warn)' }} role="img" aria-label={`${props.rating} of 5 stars`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} size={props.size ?? 15} fill={i <= props.rating ? 'currentColor' : 'none'}
          style={i <= props.rating ? undefined : { color: 'var(--ink-3)' }} />
      ))}
    </span>
  );
}

type Filter = { rating: string; from: string; to: string };

export function Reviews() {
  const { store } = useScope();
  const base = useBase();
  const [settings, setSettings] = useState<ReviewSettings | null>(null);
  const [url, setUrl] = useState('');
  const [filter, setFilter] = useState<Filter>({ rating: '', from: '', to: '' });
  const [reviews, setReviews] = useState<Review[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const seq = useRef(0);

  useEffect(() => {
    api
      .get<ReviewSettings>(`${base}/review-settings`)
      .then((s) => {
        setSettings(s);
        setUrl(s.googleReviewUrl ?? '');
      })
      .catch((e) => setError(messageOf(e, 'Failed to load the settings.')));
  }, [base]);

  const loadReviews = useCallback(async () => {
    const mine = ++seq.current;
    try {
      const rs = await api.get<Review[]>(`${base}/reviews`, {
        rating: filter.rating || undefined,
        from: filter.from || undefined,
        to: filter.to || undefined,
      });
      if (mine === seq.current) setReviews(rs);
    } catch (e) {
      if (mine === seq.current) setError(messageOf(e, 'Failed to load the reviews.'));
    }
  }, [base, filter]);

  useEffect(() => {
    void loadReviews();
  }, [loadReviews]);

  async function save() {
    if (!settings) return;
    setBusy(true);
    try {
      const s = await api.put<ReviewSettings>(`${base}/review-settings`, {
        enabled: settings.enabled,
        googleReviewUrl: url.trim() || null,
      });
      setSettings(s);
      setUrl(s.googleReviewUrl ?? '');
      setError(null);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      setError(
        e instanceof ApiError && e.code === 'review_url_required'
          ? 'Review prompts need an https Google review link — add one, or switch prompts off.'
          : messageOf(e, 'Save failed.'),
      );
    } finally {
      setBusy(false);
    }
  }

  const avg = reviews && reviews.length > 0 ? reviews.reduce((s, r) => s + r.rating, 0) / reviews.length : null;
  const filtered = !!(filter.rating || filter.from || filter.to);

  return (
    <>
      <div className="page-head">
        <div>
          <div className="overline">Customers · Reviews</div>
          <h1>Reviews</h1>
        </div>
      </div>
      {error && <div className="err" role="alert">{error}</div>}

      <div className="stats" style={{ marginBottom: 28 }}>
        <div className="stat">
          <div className="overline">Average rating{filtered ? ' · filtered' : ''}</div>
          <div className="v money" data-stat="average">{avg === null ? '—' : avg.toFixed(1)}</div>
          <div className="d">{avg === null ? 'No reviews yet' : <Stars rating={Math.round(avg)} size={13} />}</div>
        </div>
        <div className="stat">
          <div className="overline">Reviews{filtered ? ' · filtered' : ''}</div>
          <div className="v money" data-stat="count">{reviews?.length ?? '—'}</div>
          <div className="d">&nbsp;</div>
        </div>
        <div className="stat">
          <div className="overline">Prompts</div>
          <div className="v" data-stat="prompts" style={{ color: settings?.enabled ? 'var(--ok)' : 'var(--ink-3)' }}>
            {settings ? (settings.enabled ? 'On' : 'Off') : '—'}
          </div>
          <div className="d">&nbsp;</div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.5fr', gap: 20, alignItems: 'start' }}>
        <div className="panel" data-section="review-settings">
          <h2>Prompt settings {saved && <span style={{ color: 'var(--ok)', textTransform: 'none', letterSpacing: 0 }}>· saved ✓</span>}</h2>
          {settings === null && <div className="loading">Loading…</div>}
          {settings && (
            <>
              <label style={{ display: 'flex', gap: 10, alignItems: 'center', fontSize: 14, padding: '12px 0' }}>
                <input type="checkbox" checked={settings.enabled}
                  onChange={(e) => setSettings({ ...settings, enabled: e.target.checked })} />
                <b style={{ fontSize: 14 }}>Ask customers for a review after their order</b>
              </label>
              <div className="field">
                <label>Google review link</label>
                <input className="input" aria-label="Google review link" type="url" placeholder="https://g.page/r/…" value={url}
                  onChange={(e) => setUrl(e.target.value)} />
                <span style={{ color: 'var(--ink-3)', fontSize: 12.5 }}>
                  Required while prompts are on. Happy reviewers are nudged on to Google with this link.
                </span>
              </div>
              <button className="btn btn-primary" style={{ marginTop: 14 }} disabled={busy} onClick={() => void save()}>
                {busy ? 'Saving…' : 'Save settings'}
              </button>
            </>
          )}
        </div>

        <div className="panel" data-section="reviews">
          <h2>Review stream</h2>
          <div className="filters" style={{ padding: '10px 0', display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            <select className="input" aria-label="Filter by rating" style={{ width: 'auto' }} value={filter.rating}
              onChange={(e) => setFilter({ ...filter, rating: e.target.value })}>
              <option value="">Any rating</option>
              {[5, 4, 3, 2, 1].map((n) => (
                <option key={n} value={String(n)}>{n} star{n === 1 ? '' : 's'}</option>
              ))}
            </select>
            <input className="input" aria-label="From date" type="date" style={{ width: 'auto' }} value={filter.from}
              onChange={(e) => setFilter({ ...filter, from: e.target.value })} />
            <span style={{ color: 'var(--ink-3)' }}>→</span>
            <input className="input" aria-label="To date" type="date" style={{ width: 'auto' }} value={filter.to}
              onChange={(e) => setFilter({ ...filter, to: e.target.value })} />
            {filtered && (
              <button className="btn btn-ghost btn-sm" onClick={() => setFilter({ rating: '', from: '', to: '' })}>
                Clear
              </button>
            )}
          </div>
          {reviews === null && <div className="loading">Loading…</div>}
          {reviews?.length === 0 && <div className="loading">{filtered ? 'No reviews match.' : 'No reviews yet.'}</div>}
          {reviews?.map((r) => (
            <div key={r.reviewId} className="rowline" data-rating={r.rating} style={{ alignItems: 'flex-start' }}>
              <Stars rating={r.rating} />
              <div className="grow">
                <b>{r.customerName ?? 'Anonymous'}</b>
                {r.comment && <span className="sub" style={{ fontSize: 13.5, color: 'var(--ink-2)' }}>“{r.comment}”</span>}
                <span className="sub">
                  {dateTimeOf(r.createdAt, store?.timezone)}
                  {r.orderId ? ` · order ${r.orderId}` : ''}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}
